// qs-store-canvas.ts
import type { KonvaLine, KonvaStage } from '@/types/qs-type-konva'
import type { DrawingPath, Point } from '@/types/qs-types-index'
import { TaperingEngine } from '@/brushes/engines/qs-brush-engine-tapering'
import { useZoomPerformance } from '@/composables/qs-useZoomPerformance'
import { useToolStore } from '@/stores/qs-store-tools'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

type ZoomOptions = {
  forceImmediate?: boolean
  enableQualityScaling?: boolean
  enableThrottling?: boolean
}

export const useCanvasStore = defineStore('canvas', () => {
  const toolStore = useToolStore()
  const zoomPerformance = useZoomPerformance()

  zoomPerformance.startPerformanceMonitoringManually()

  const stage = ref<KonvaStage | null>(null)
  const scale = useStorage('vectorsketch-canvas-scale', 1)
  const position = useStorage<Point>('vectorsketch-canvas-position', { x: 0, y: 0 })
  const width = ref(window.innerWidth)
  const height = ref(window.innerHeight)
  const isDrawing = ref(false)
  const isPanning = ref(false)
  const currentPath = ref<DrawingPath | null>(null)
  const brushLines = shallowRef<KonvaLine[]>([])
  const isKeyPressed = ref<Record<string, boolean>>({})
  const isPanelHovered = ref(false)
  const lastPanPosition = ref<Point | null>(null)

  const backgroundColor = computed(() => toolStore.backgroundColor)

  const canvasState = computed(() => ({
    stage: stage.value,
    scale: scale.value,
    position: position.value,
    isDrawing: isDrawing.value,
    isPanning: isPanning.value,
    currentPath: currentPath.value
  }))

  const viewportBounds = computed(() => ({
    x: -position.value.x / scale.value,
    y: -position.value.y / scale.value,
    width: width.value / scale.value,
    height: height.value / scale.value
  }))

  const setStage = (newStage: KonvaStage | null) => {
    if (stage.value && stage.value !== newStage) {
      try {
        stage.value.destroy()
      } catch {
      }
    }
    stage.value = newStage
  }

  const setScale = (newScale: number) => {
    scale.value = newScale
  }

  const setPosition = (newPosition: Point) => {
    position.value = newPosition
  }

  const setDrawing = (drawing: boolean) => {
    isDrawing.value = drawing
  }

  const setPanning = (panning: boolean) => {
    isPanning.value = panning
  }

  const setCurrentPath = (path: DrawingPath | null) => {
    currentPath.value = path
  }

  const setBrushLines = (lines: KonvaLine[]) => {
    brushLines.value = lines
  }

  const setKeyPressed = (key: string, pressed: boolean) => {
    if (pressed) {
      isKeyPressed.value[key] = true
    } else {
      delete isKeyPressed.value[key]
    }
  }

  const setPanelHovered = (hovered: boolean) => {
    isPanelHovered.value = hovered
  }

  const setLastPanPosition = (pos: Point | null) => {
    lastPanPosition.value = pos
  }

  const setBackgroundColor = (color: string) => {
    toolStore.setBackgroundColor(color)
    const container = document.querySelector('.drawing-page') as HTMLElement | null
    if (container) {
      container.style.backgroundColor = color
    }
  }

  const updateDimensions = () => {
    const newWidth = window.innerWidth
    const newHeight = window.innerHeight

    width.value = newWidth
    height.value = newHeight

    if (stage.value) {
      stage.value.width(newWidth)
      stage.value.height(newHeight)
      stage.value.batchDraw()
    }
  }

  const zoom = (factor: number, center?: Point, options: ZoomOptions = {}) => {
    const newScale = Math.max(0.1, Math.min(10, scale.value * factor))

    TaperingEngine.onZoomActivity()

    const zoomFunction = () => {
      if (center && stage.value) {
        const worldPos: Point = {
          x: (center.x - position.value.x) / scale.value,
          y: (center.y - position.value.y) / scale.value
        }

        const newPos: Point = {
          x: center.x - worldPos.x * newScale,
          y: center.y - worldPos.y * newScale
        }

        setScale(newScale)
        setPosition(newPos)
        stage.value.scale({ x: newScale, y: newScale })
        stage.value.position(newPos)
        stage.value.batchDraw()
      } else {
        setScale(newScale)
        if (stage.value) {
          stage.value.scale({ x: newScale, y: newScale })
          stage.value.batchDraw()
        }
      }
    }

    zoomPerformance.optimizedZoom(zoomFunction, stage.value, newScale, {
      forceImmediate: options.forceImmediate ?? false,
      enableQualityScaling: options.enableQualityScaling ?? true,
      enableThrottling: options.enableThrottling ?? true
    })
  }

  const pan = (deltaX: number, deltaY: number) => {
    const newPosition: Point = {
      x: position.value.x + deltaX,
      y: position.value.y + deltaY
    }
    setPosition(newPosition)

    if (stage.value) {
      stage.value.position(newPosition)
      stage.value.batchDraw()
    }
  }

  const resetView = () => {
    setScale(1)
    setPosition({ x: 0, y: 0 })

    if (stage.value) {
      stage.value.scale({ x: 1, y: 1 })
      stage.value.position({ x: 0, y: 0 })
      stage.value.batchDraw()
    }
  }

  const centerArtboard = () => {
    resetView()
  }

  const fitToScreen = () => {
    resetView()
  }

  const cleanup = () => {
    zoomPerformance.stopPerformanceMonitoringManually()
    
    import('@/brushes/engines/qs-brush-engine-tapering').then(({ TaperingEngine }) => {
      TaperingEngine.cleanupSharpnessTimeouts()
      TaperingEngine.clearAllCaches()
    })
    
    if (stage.value) {
      try {
        stage.value.destroy()
        stage.value = null
      } catch {
      }
    }
    brushLines.value = []
    isKeyPressed.value = {}
    currentPath.value = null
    isDrawing.value = false
    isPanning.value = false
    lastPanPosition.value = null
  }

  return {
    stage,
    scale,
    position,
    width,
    height,
    isDrawing,
    isPanning,
    currentPath,
    brushLines,
    isKeyPressed,
    isPanelHovered,
    lastPanPosition,
    backgroundColor,
    canvasState,
    viewportBounds,
    setStage,
    setScale,
    setPosition,
    setDrawing,
    setPanning,
    setCurrentPath,
    setBrushLines,
    setKeyPressed,
    setPanelHovered,
    setLastPanPosition,
    setBackgroundColor,
    updateDimensions,
    zoom,
    pan,
    resetView,
    centerArtboard,
    fitToScreen,
    cleanup,
    zoomPerformance
  }
})